import React from "react"
import {Box, Typography, useTheme} from "@mui/material"
import {calculateDateProgressPercentage} from "./time-utils"

const DOT_INTERVAL = 5
const AXIS_TOP_HEIGHT = 22
const AXIS_BOTTOM_HEIGHT = 40
const AXIS_HEIGHT = AXIS_TOP_HEIGHT + AXIS_BOTTOM_HEIGHT

const YearTick = ({leftPercent, year, colors}) => (
    <Box
        sx={{
            position: "absolute",
            left: `${leftPercent}%`,
            bottom: `${AXIS_BOTTOM_HEIGHT}px`,
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
        }}
    >
        <Typography variant="caption" sx={{color: colors.yearTextColor, lineHeight: 1}}>
            {year}
        </Typography>
        <Box
            sx={{
                width: "2px",
                height: "8px",
                backgroundColor: colors.tickColor,
                mt: 0.5,
            }}
        />
    </Box>
)

const SpecialMarker = ({leftPercent, label, colors}) => (
    <Box
        sx={{
            position: "absolute",
            left: `${leftPercent}%`,
            top: `${AXIS_TOP_HEIGHT}px`,
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 2,
        }}
    >
        <Box
            sx={{
                width: "3px",
                height: "14px",
                backgroundColor: colors.markerColor,
            }}
        />
        <Box
            sx={{
                backgroundColor: colors.markerColor,
                color: colors.markerTextColor,
                borderRadius: "12px",
                px: 1.5,
                py: 0.25,
                mt: 0.5,
                whiteSpace: "nowrap",
            }}
        >
            <Typography variant="caption" sx={{color: colors.markerTextColor, fontWeight: 500}}>
                {label}
            </Typography>
        </Box>
    </Box>
)

const TimelineAxis = ({startYear, endYear, rentebeginn, leftOffsetPx, widthPx}) => {
    const theme = useTheme()
    const colors = theme.EVENTSANDBARCHART?.axis ?? {}
    const totalYears = endYear - startYear
    const years = Array.from(
        {length: Math.floor(totalYears / DOT_INTERVAL) + 1},
        (_, i) => startYear + i * DOT_INTERVAL,
    )
    const now = new Date()
    const todayISO = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
    const todayPercent = calculateDateProgressPercentage(todayISO, startYear, endYear)
    const rentePercent = rentebeginn
        ? calculateDateProgressPercentage(rentebeginn, startYear, endYear)
        : null

    return (
        <Box sx={{position: "relative", height: `${AXIS_HEIGHT}px`, mt: 1}}>
            <Box
                sx={{
                    position: "absolute",
                    left: `${leftOffsetPx}px`,
                    width: `${widthPx}px`,
                    top: 0,
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: `${AXIS_TOP_HEIGHT}px`,
                        height: "1px",
                        backgroundColor: colors.lineColor,
                    }}
                />
                {years.map((year) => {
                    const pct = calculateDateProgressPercentage(`${year}-01-01`, startYear, endYear)
                    return <YearTick key={year} leftPercent={pct} year={year} colors={colors} />
                })}
                {todayPercent >= 0 && todayPercent <= 100 && (
                    <SpecialMarker leftPercent={todayPercent} label="heute" colors={colors} />
                )}
                {rentePercent !== null && rentePercent >= 0 && rentePercent <= 100 && (
                    <SpecialMarker leftPercent={rentePercent} label="Rentebeginn" colors={colors} />
                )}
            </Box>
        </Box>
    )
}

export default TimelineAxis
